<script setup lang="ts">
import { computed, ref } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import {
  toFlexibleNullableNumber,
  isRecord,
  toLocalizedNullableNumber,
  toNullableString,
  toStringArray,
} from '@/utils/coerce';
import { formatCurrency } from '@/utils/currency';
import { formatDate, formatNumber } from '@/utils/formatters';
import { getRiskLevelDisplay, toTitleCase } from '@/utils/kyc';
import type { KycCategory } from '@/types/kyc';

interface LawsuitRecord {
  type: string | null;
  state: string | null;
  value: number | null;
  status: string | null;
  ruleId: string | null;
  closeDate: string | null;
  courtName: string | null;
  levelValue: number | null;
  processNumber: string;
  caseDescription: string | null;
  publicationDate: string | null;
  caseCategories: string[];
  completionYearsSince: number | null;
}

interface LawsuitGroup {
  processNumber: string;
  courtName: string | null;
  state: string | null;
  type: string | null;
  records: LawsuitRecord[];
  maxLevelValue: number | null;
  maxValue: number | null;
  statusSortValue: number;
}

interface LawsuitTableRow extends LawsuitRecord {
  rowKey: string;
  groupCourtName: string | null;
  groupState: string | null;
  groupType: string | null;
  groupMaxLevelValue: number | null;
  groupMaxValue: number | null;
  groupRowCount: number;
  statusSortValue: number;
}

interface Props {
  category: KycCategory;
}

const props = defineProps<Props>();
const INITIAL_VISIBLE_GROUPS = 5;
const sortField = ref<'processNumber' | 'groupMaxLevelValue' | 'statusSortValue' | 'groupMaxValue'>('groupMaxLevelValue');
const sortOrder = ref(-1);
const showAllGroups = ref(false);

const normalizeLabel = (value: string | null): string => {
  if (!value) return 'Não informado';

  const labelOverrides: Record<string, string> = {
    ARQUIVADO: 'Arquivado',
    ATIVO: 'Ativo',
    BAIXADO: 'Baixado',
    ENCERRADO: 'Encerrado',
    SUSPENSO: 'Suspenso',
    DESAPROPRIACAO: 'Desapropriação',
  };

  if (labelOverrides[value]) return labelOverrides[value];

  return toTitleCase(value.toLocaleLowerCase('pt-BR').replace(/_/g, ' '));
};

const getStatusSortValue = (status: string | null): number => {
  const normalized = status?.toLocaleLowerCase('pt-BR').replace(/_/g, ' ') ?? '';
  if (['ativo', 'em andamento'].includes(normalized)) return 4;
  if (normalized === 'suspenso') return 3;
  if (normalized === 'arquivado') return 2;
  if (['baixado', 'encerrado'].includes(normalized)) return 1;
  return 0;
};

const normalizeRecord = (item: Record<string, unknown>): LawsuitRecord => ({
  type: toNullableString(item.type),
  state: toNullableString(item.state),
  value: toFlexibleNullableNumber(item.value),
  status: toNullableString(item.status),
  ruleId: toNullableString(item.rule_id),
  closeDate: toNullableString(item.close_date),
  courtName: toNullableString(item.court_name),
  levelValue: toLocalizedNullableNumber(item.level_value),
  processNumber: toNullableString(item.process_number) ?? 'Processo sem número',
  caseDescription: toNullableString(item.case_description),
  publicationDate: toNullableString(item.publication_date),
  caseCategories: toStringArray(item.case_category_list),
  completionYearsSince: toLocalizedNullableNumber(item.process_completion_years_since),
});

const unwrapLawsuitItem = (item: Record<string, unknown>): Record<string, unknown> | null => {
  if (isRecord(item._custom) && isRecord(item._custom.value)) {
    return item._custom.value;
  }
  return item;
};

const lawsuitRecords = computed<LawsuitRecord[]>(() => {
  const raw = props.category.data_json;
  const items = Array.isArray(raw)
    ? raw
    : isRecord(raw)
      ? raw.lawsuits ?? raw.items ?? raw.processes
      : [];

  if (!Array.isArray(items)) return [];

  return items
    .filter(isRecord)
    .map(unwrapLawsuitItem)
    .filter(isRecord)
    .map(normalizeRecord);
});

const lawsuitGroups = computed<LawsuitGroup[]>(() => {
  const groups = new Map<string, LawsuitRecord[]>();

  for (const record of lawsuitRecords.value) {
    const current = groups.get(record.processNumber) ?? [];
    current.push(record);
    groups.set(record.processNumber, current);
  }

  return Array.from(groups.entries())
    .map(([processNumber, records]) => {
      const orderedRecords = [...records].sort((left, right) => (left.levelValue ?? 99) - (right.levelValue ?? 99));
      const firstRecord = orderedRecords[0];
      const levelValues = orderedRecords
        .map((record) => record.levelValue)
        .filter((value): value is number => value !== null);
      const claimValues = orderedRecords
        .map((record) => record.value)
        .filter((value): value is number => value !== null);

      return {
        processNumber,
        records: orderedRecords,
        courtName: firstRecord?.courtName ?? null,
        state: firstRecord?.state ?? null,
        type: firstRecord?.type ?? null,
        maxLevelValue: levelValues.length ? Math.max(...levelValues) : null,
        maxValue: claimValues.length ? Math.max(...claimValues) : null,
        statusSortValue: orderedRecords.reduce((highest, record) => Math.max(highest, getStatusSortValue(record.status)), 0),
      };
    });
});

const sortedLawsuitGroups = computed<LawsuitGroup[]>(() => {
  const direction = sortOrder.value >= 0 ? 1 : -1;

  return [...lawsuitGroups.value]
    .map((group) => ({
      ...group,
      records: [...group.records].sort((left, right) => {
        if (sortField.value === 'statusSortValue') {
          const statusCompare = (getStatusSortValue(left.status) - getStatusSortValue(right.status)) * direction;
          if (statusCompare !== 0) return statusCompare;
        }

        if (sortField.value === 'groupMaxValue') {
          const valueCompare = ((left.value ?? -1) - (right.value ?? -1)) * direction;
          if (valueCompare !== 0) return valueCompare;
        }

        return (left.levelValue ?? 99) - (right.levelValue ?? 99);
      }),
    }))
    .sort((left, right) => {
      if (sortField.value === 'processNumber') {
        return left.processNumber.localeCompare(right.processNumber) * direction;
      }

      if (sortField.value === 'statusSortValue') {
        const statusCompare = (left.statusSortValue - right.statusSortValue) * direction;
        if (statusCompare !== 0) return statusCompare;
      }

      if (sortField.value === 'groupMaxLevelValue') {
        const riskCompare = ((left.maxLevelValue ?? -1) - (right.maxLevelValue ?? -1)) * direction;
        if (riskCompare !== 0) return riskCompare;
      }

      if (sortField.value === 'groupMaxValue') {
        const valueCompare = ((left.maxValue ?? -1) - (right.maxValue ?? -1)) * direction;
        if (valueCompare !== 0) return valueCompare;
      }

      return left.processNumber.localeCompare(right.processNumber);
    });
});

const visibleLawsuitGroups = computed<LawsuitGroup[]>(() => (
  showAllGroups.value ? sortedLawsuitGroups.value : sortedLawsuitGroups.value.slice(0, INITIAL_VISIBLE_GROUPS)
));

const lawsuitTableRows = computed<LawsuitTableRow[]>(() =>
  visibleLawsuitGroups.value.flatMap((group) =>
    group.records.map((record, index) => ({
      ...record,
      rowKey: `${group.processNumber}-${record.levelValue ?? 'sem-grau'}-${record.ruleId ?? record.publicationDate ?? index}`,
      groupCourtName: group.courtName,
      groupState: group.state,
      groupType: group.type,
      groupMaxLevelValue: group.maxLevelValue,
      groupMaxValue: group.maxValue,
      groupRowCount: group.records.length,
      statusSortValue: getStatusSortValue(record.status),
    }))
  )
);

const hiddenGroupCount = computed(() => Math.max(sortedLawsuitGroups.value.length - visibleLawsuitGroups.value.length, 0));

const formatLevel = (levelValue: number | null): string => {
  if (levelValue === null) return 'Grau não informado';
  return `${levelValue}º grau`;
};

const formatYearsSince = (value: number | null): string => {
  if (value === null) return '';
  const rounded = Number(value.toFixed(1));
  const formatted = formatNumber(rounded);
  return rounded === 1 ? 'há 1 ano' : `há ${formatted} anos`;
};

const formatRuleId = (ruleId: string | null): string => ruleId ? ruleId.slice(0, 8) : '';

const getStatusClasses = (status: string | null): string => {
  const normalized = status?.toLocaleLowerCase('pt-BR').replace(/_/g, ' ') ?? '';
  if (['arquivado', 'baixado', 'encerrado'].includes(normalized)) {
    return 'border-slate-200 bg-slate-50 text-slate-700';
  }
  if (['ativo', 'em andamento'].includes(normalized)) {
    return 'border-amber-200 bg-amber-50 text-amber-800';
  }
  if (['suspenso'].includes(normalized)) {
    return 'border-blue-200 bg-blue-50 text-blue-800';
  }
  return 'border-slate-200 bg-white text-slate-600';
};

const getRiskTagAttrs = (levelValue: number | null) => {
  const display = getRiskLevelDisplay(levelValue);
  return {
    value: display.label,
    style: {
      backgroundColor: display.hexBgColor,
      color: display.hexColor,
    },
    class: [display.borderClass, 'border'],
  };
};

const handleSort = (event: { sortField?: string | ((item: any) => string); sortOrder?: number | null }) => {
  const nextSortField = typeof event.sortField === 'string' ? event.sortField : 'processNumber';
  if (
    nextSortField === 'processNumber' ||
    nextSortField === 'groupMaxLevelValue' ||
    nextSortField === 'statusSortValue' ||
    nextSortField === 'groupMaxValue'
  ) {
    sortField.value = nextSortField;
  } else {
    sortField.value = 'processNumber';
  }
  sortOrder.value = event.sortOrder ?? 1;
};

const handleToggleGroupVisibility = () => {
  showAllGroups.value = !showAllGroups.value;
};
</script>

<template>
  <div class="mt-3">
    <div
      v-if="lawsuitTableRows.length"
      class="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
    >
      <div
        v-if="sortedLawsuitGroups.length > INITIAL_VISIBLE_GROUPS"
        class="flex items-center justify-between gap-3 border-b border-slate-200 px-3 py-2"
      >
        <span class="text-[11px] text-slate-500">
          Mostrando {{ visibleLawsuitGroups.length }} de {{ sortedLawsuitGroups.length }} processos
        </span>
        <BaseButton
          :icon="showAllGroups ? 'unfold_less' : 'unfold_more'"
          :label="showAllGroups ? 'Mostrar menos' : `Mostrar mais ${hiddenGroupCount > 0 ? `(${hiddenGroupCount})` : ''}`"
          size="small"
          severity="secondary"
          text
          @click="handleToggleGroupVisibility"
        />
      </div>

      <DataTable
        :value="lawsuitTableRows"
        dataKey="rowKey"
        rowGroupMode="rowspan"
        groupRowsBy="processNumber"
        customSort
        :sortField="sortField"
        :sortOrder="sortOrder"
        class="p-datatable-sm text-[12px]"
        tableStyle="width: 100%; table-layout: fixed"
        @sort="handleSort"
      >
        <Column field="processNumber" header="Processo" sortable style="width: 23%">
          <template #body="{ data }: { data: LawsuitTableRow }">
            <div class="flex min-w-0 flex-col gap-1 py-1">
              <div class="flex flex-wrap items-center gap-1.5">
                <span class="truncate font-mono text-[12px] font-semibold text-slate-950">{{ data.processNumber }}</span>
                <span class="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                  {{ data.groupRowCount }} grau{{ data.groupRowCount === 1 ? '' : 's' }}
                </span>
              </div>

              <div class="flex flex-col gap-0.5 text-[11px] text-slate-500">
                <span class="inline-flex min-w-0 items-center gap-1">
                  <BaseIcon class="text-sm text-slate-400">account_balance</BaseIcon>
                  <span class="truncate">{{ data.groupCourtName || 'Tribunal não informado' }}</span>
                </span>
                <span class="inline-flex min-w-0 items-center gap-1">
                  <BaseIcon class="text-sm text-slate-400">topic</BaseIcon>
                  <span class="truncate">
                    {{ normalizeLabel(data.groupType) }}<span v-if="data.groupState"> · {{ data.groupState }}</span>
                  </span>
                </span>
              </div>
            </div>
          </template>
        </Column>

        <Column field="groupMaxLevelValue" header="Risco" sortable style="width: 10%">
          <template #body="{ data }: { data: LawsuitTableRow }">
            <Tag v-if="data.groupMaxLevelValue !== null" v-bind="getRiskTagAttrs(data.groupMaxLevelValue)" />
            <span v-else class="text-[11px] text-slate-500">Não informado</span>
          </template>
        </Column>

        <Column field="levelValue" header="Grau" style="width: 11%">
          <template #body="{ data }: { data: LawsuitTableRow }">
            <div class="flex items-center gap-2">
              <span
                class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-[11px] font-bold text-slate-700"
              >
                {{ data.levelValue ?? '-' }}
              </span>
              <div class="min-w-0">
                <div class="text-[11px] font-semibold text-slate-900">{{ formatLevel(data.levelValue) }}</div>
                <div v-if="formatRuleId(data.ruleId)" class="font-mono text-[10px] text-slate-400">
                  {{ formatRuleId(data.ruleId) }}
                </div>
              </div>
            </div>
          </template>
        </Column>

        <Column field="statusSortValue" header="Status" sortable style="width: 12%">
          <template #body="{ data }: { data: LawsuitTableRow }">
            <span
              :class="[
                'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold',
                getStatusClasses(data.status)
              ]"
            >
              {{ normalizeLabel(data.status) }}
            </span>
          </template>
        </Column>

        <Column header="Descrição" style="width: 26%">
          <template #body="{ data }: { data: LawsuitTableRow }">
            <div class="min-w-0 py-1">
              <p class="line-clamp-2 text-[11px] leading-4 text-slate-700">
                {{ normalizeLabel(data.caseDescription) }}
              </p>
              <div v-if="data.caseCategories.length" class="mt-1 flex flex-wrap gap-1">
                <span
                  v-for="categoryName in data.caseCategories"
                  :key="categoryName"
                  class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                >
                  {{ normalizeLabel(categoryName) }}
                </span>
              </div>
            </div>
          </template>
        </Column>

        <Column header="Datas" style="width: 12%">
          <template #body="{ data }: { data: LawsuitTableRow }">
            <div class="py-1 text-[11px]">
              <div>
                <span class="font-medium text-slate-500">Pub.</span>
                <span class="ml-1 font-medium text-slate-800">{{ formatDate(data.publicationDate, undefined, 'Sem data') }}</span>
              </div>
              <div class="mt-0.5">
                <span class="font-medium text-slate-500">Enc.</span>
                <span class="ml-1 font-medium text-slate-800">{{ formatDate(data.closeDate, undefined, 'Sem data') }}</span>
              </div>
              <div v-if="formatYearsSince(data.completionYearsSince)" class="mt-0.5 text-[10px] text-slate-500">
                {{ formatYearsSince(data.completionYearsSince) }}
              </div>
            </div>
          </template>
        </Column>

        <Column field="groupMaxValue" header="Valor" sortable style="width: 6%">
          <template #body="{ data }: { data: LawsuitTableRow }">
            <span class="text-[11px] font-semibold text-slate-800">
              {{ data.value === null ? 'Não informado' : formatCurrency(data.value, 'BRL', true) }}
            </span>
          </template>
        </Column>
      </DataTable>
    </div>

    <div v-else class="text-sm italic text-slate-500">
      Nenhum processo judicial encontrado.
    </div>
  </div>
</template>
